import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Row,
  Col,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faEdit } from "@fortawesome/free-solid-svg-icons";
import CalendarModal from "./CalendarModal";
import EquipmentViewModal from "../Modal/EquipmentViewModal";

const CustomCalendarPage = ({
  holidayMap = {},
  setHolidayMap = () => {},
  isHolidayLoading = false,
  fetchHolidayInfo = () => {},
  event,
  plantIdFromApi,
  eventColorMap,
}) => {
  const year = new Date().getFullYear();
  const nextYear = year + 1;
  const prevYear = year - 1;
  const scrollRef = useRef(null);

  const [prevScrollTop, setPrevScrollTop] = useState(0);
  const [displayYear, setDisplayYear] = useState(year);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showFullList, setShowFullList] = useState(false);
  const [openEqViewModal, setOpenEqViewModal] = useState(false);
  const [modalPos, setModalPos] = useState({ left: 0 });
  const [arrowTop, setArrowTop] = useState(0);

  const [selectedEquip, setSelectedEquip] = useState("");
  const [hoveredMonthIndex, setHoveredMonthIndex] = useState(null);
  const [selectedSchool, setSelectedSchool] = useState("富原中学校");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggleDropdown = () => setDropdownOpen(!dropdownOpen);

  const schools = [
    "富原中学校",
    "富原小学校",
    "富原小学校（暖房）",
    "学校 温水プール・総合体育館",
    "釧路町役場",
  ];

  const schoolToPlantId = {
    富原中学校: "MG_PLANT_1",
    富原小学校: "MG_PLANT_2",
    "富原小学校（暖房）": "MG_PLANT_3",
    "学校 温水プール・総合体育館": "MG_PLANT_4",
    釧路町役場: "YB_PLANT_1",
  };

  const plantId = schoolToPlantId[selectedSchool];

  const plantName = Object.keys(schoolToPlantId).find(
    (name) => schoolToPlantId[name] === plantIdFromApi,
  );

  const weekdayLabels = ["日", "月", "火", "水", "木", "金", "土"];

  const handleYearClick = (selectedYear) => {
    setShowFullList(true);
    setDisplayYear(selectedYear);
    fetchHolidayInfo(selectedYear, plantId);
  };

  const openModalForMonth = (monthDate, calendarIndex) => {
    setSelectedMonth(monthDate);
    setIsModalOpen(true);
    localStorage.setItem("lastCalendarIndex", calendarIndex);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const onScroll = () => {
    const scrollTop = scrollRef.current.scrollTop;
    if (scrollTop > prevScrollTop) {
      console.log("Scrolling down ");
    } else if (scrollTop < prevScrollTop) {
      if (!showFullList) setShowFullList(true);
    }
    setPrevScrollTop(scrollTop);
  };

  const monthBlocks = [];
  for (let i = 0; i < 24; i += 3) monthBlocks.push([i, i + 1, i + 2]);
  const monthsToRender = monthBlocks.flat();

  const formatDateKey = (date) =>
    date.getFullYear() +
    "-" +
    String(date.getMonth() + 1).padStart(2, "0") +
    "-" +
    String(date.getDate()).padStart(2, "0");

  // const toDateOnly = (dateStr) => {
  //   const d = new Date(dateStr.replace(/-/g, "/"));
  //   d.setHours(0, 0, 0, 0);
  //   return d;
  // };
  const toDateOnly = (dateInput) => {
    let d;

    if (typeof dateInput === "string") {
      d = new Date(dateInput.replace(/-/g, "/"));
    } else {
      d = new Date(dateInput); // already a Date object
    }

    d.setHours(0, 0, 0, 0);
    return d;
  };

  const isDateInRange = (cellDate, start, end) => {
    const d = new Date(cellDate);
    d.setHours(0, 0, 0, 0);
    return d >= start && d <= end;
  };

  const handleOpenView = (e, data) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setModalPos({ left: rect.right });
    setArrowTop(rect.top + rect.height / 2);
    setSelectedEquip(data);
    setOpenEqViewModal(true);
  };
  const handleCloseView = () => {
    setSelectedEquip("");
    setOpenEqViewModal(false);
  };

  const generateMonthGrid = (yearNum, monthNum) => {
    const firstDay = new Date(yearNum, monthNum, 1).getDay(); // 0..6
    const daysInMonth = new Date(yearNum, monthNum + 1, 0).getDate();

    const cells = Array.from({ length: 42 }, () => ({
      day: null,
      date: null,
      inMonth: false,
    }));

    for (let d = 1; d <= daysInMonth; d++) {
      const idx = firstDay + (d - 1);
      cells[idx] = {
        day: d,
        date: new Date(yearNum, monthNum, d),
        inMonth: true,
      };
    }

    const topIndices = Array.from({ length: 7 }, (_, i) => i);
    const bottomIndices = Array.from({ length: 7 }, (_, i) => 35 + i);

    const topEmptySlots = topIndices.filter((i) => !cells[i].day);
    const bottomFilledSlots = bottomIndices.filter((i) => !!cells[i].day);

    if (topEmptySlots.length > 0 && bottomFilledSlots.length > 0) {
      const carry = bottomFilledSlots.map((i) => cells[i]);
      let ci = 0;
      for (let tIdx of topEmptySlots) {
        if (ci >= carry.length) break;
        cells[tIdx] = carry[ci++];
      }

      let removed = 0;
      for (let i of bottomIndices) {
        if (removed >= ci) break;
        if (cells[i].day !== null && cells[i].day > daysInMonth - 7) {
          cells[i] = { day: null, date: null, inMonth: false };
          removed++;
        } else if (cells[i].day !== null && removed < ci) {
          cells[i] = { day: null, date: null, inMonth: false };
          removed++;
        }
      }
    }

    return cells;
  };

  const colors = ["#EFEC3A", "#00F1B5", "#E26EFF", "#FFA928", "#00FF40"];

  const renderMonthBox = (monthDate, i, calendarIndex) => {
    const y = monthDate.getFullYear();
    const m = monthDate.getMonth();
    const cells = generateMonthGrid(y, m);

    return (
      <div
        id={`calendar-month-${calendarIndex}`}
        key={calendarIndex}
        className="customCalendar-box"
        style={{
          border: "1px solid #ccc",
          borderRadius: 6,
          position: "relative",
        }}
        onMouseEnter={() => setHoveredMonthIndex(i)}
        onMouseLeave={() => setHoveredMonthIndex(null)}
      >
        {hoveredMonthIndex === i && (
          <button
            onClick={() => openModalForMonth(monthDate, calendarIndex)}
            className="customOk-btn "
            aria-label="edit-month"
          >
            <FontAwesomeIcon icon={faEdit} />
          </button>
        )}

        <div className="customCalendar-header">
          {y}年{m + 1}月
        </div>

        <div className="customWeekday-row">
          {weekdayLabels.map((w) => (
            <div key={w} style={{ textAlign: "center" }}>
              {w}
            </div>
          ))}
        </div>

        <div className="customMonth-grid">
          {cells.map((cell, idx) => {
            const isWeekend = (() => {
              if (!cell.date) return false;
              const d = cell.date.getDay();
              return d === 0 || d === 6;
            })();

            const key = cell.date ? formatDateKey(cell.date) : null;
            const holidayInfo = key && holidayMap ? holidayMap[key] : null;
            let dayEvents = [];

            if (cell.date && Array.isArray(event)) {
              dayEvents = event.filter((ev) => {
                const start = toDateOnly(ev.start_time);
                // const end = toDateOnly(ev.end_time);
                let end = new Date(ev.end_time);
                if (
                  end.getHours() === 0 &&
                  end.getMinutes() === 0 &&
                  end.getSeconds() === 0
                ) {
                  end.setDate(end.getDate() - 1);
                }

                end = toDateOnly(end);
                return isDateInRange(cell.date, start, end);
              });
            }
            const classes = ["customDay-cell1"];
            if (cell.day !== null && cell.inMonth)
              classes.push("customDay-inmonth ");
            // if (isWeekend) classes.push("customWeekend");
            if (holidayInfo) classes.push("customHoliday");

            return (
              <div
                key={idx}
                className={classes.join(" ")}
                style={{
                  position: "relative",
                  padding: "6px",
                  paddingBottom: "3px",
                }}
                onClick={(e) =>
                  dayEvents?.length && handleOpenView(e, dayEvents)
                }
              >
                {cell.day !== null ? (
                  <div>{cell.day}</div>
                ) : (
                  <div style={{ opacity: 0.08 }}>&nbsp;</div>
                )}
                <div className="infoDiv">
                  {holidayInfo && (
                    <div className="customHoliday-label">{holidayInfo}</div>
                  )}
                </div>

                <div className="infoDiv">
                  {dayEvents.length > 0 &&
                    dayEvents.map((event, index) => {
                      const color = eventColorMap[event.event_id];

                      return (
                        <div
                          key={`${event.event_id}-${cell.date}`}
                          style={{ backgroundColor: color }}
                          className="customEvent-label"
                          // onClick={(e) => handleOpenView(e, event)}
                        ></div>
                      );
                    })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  useEffect(() => {
    const shouldRestore = sessionStorage.getItem("restoreCalendarPosition");
    if (!shouldRestore) return;

    const savedIndex = localStorage.getItem("lastCalendarIndex");
    if (savedIndex == null) return;

    setTimeout(() => {
      const container = scrollRef.current;
      if (!container) return;

      const boxes = container.querySelectorAll(".customCalendar-box");
      const target = boxes[Number(savedIndex)];
      if (!target) return;

      container.scrollTo({
        top: target.offsetTop - container.offsetTop,
        behavior: "smooth",
      });

      sessionStorage.removeItem("restoreCalendarPosition");
    }, 100);
  }, [holidayMap]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const MONTHS_PER_ROW = 3;
    const now = new Date();

    const currentMonthIndex =
      now.getFullYear() === displayYear ? now.getMonth() : 12 + now.getMonth();

    const currentRow = Math.floor(currentMonthIndex / MONTHS_PER_ROW);
    const targetRow = Math.max(currentRow - 1, 0);
    const targetMonthIndex = targetRow * MONTHS_PER_ROW;

    const target = document.getElementById(
      `calendar-month-${targetMonthIndex}`,
    );

    if (!target) return;

    container.scrollTo({
      top: target.offsetTop - container.offsetTop,
      behavior: "auto",
    });
  }, [displayYear]);

  return (
    <>
      {isHolidayLoading ? (
        <div className="loading">
          <div className="loader "></div>
        </div>
      ) : (
        <>
          <Row className="my-3  d-flex justify-content-between align-items-center  ">
            <Col md={9} xxl={8} className="ps-0">
              <Row
                className="infoFormCalendar   d-flex flex-wrap align-items-center justify-content-between"
                style={{ height: "42px", marginInline: "5px" }}
              >
                <Col
                  xs={7}
                  md={8}
                  xl={9}
                  className="d-flex align-items-center justify-content-between pe-0"
                >
                  <span className="selectedSchool">
                    {plantName}休日情報カレンダー
                  </span>

                  <Dropdown
                    isOpen={dropdownOpen}
                    toggle={toggleDropdown}
                    className="school-show"
                  >
                    <DropdownToggle
                      size="sm"
                      className="dropDown d-flex justify-content-between align-items-center"
                    >
                      {selectedSchool}
                      <FontAwesomeIcon icon={faChevronDown} size="xs" />
                    </DropdownToggle>
                    <DropdownMenu>
                      {schools.map((school, index) => (
                        <DropdownItem
                          key={index}
                          onClick={() => setSelectedSchool(school)}
                          active={selectedSchool === school}
                        >
                          {school}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  </Dropdown>
                </Col>
                <Col xs={5} md={4} xl={3} className="text-end">
                  <Button
                    className="displayButton btn-sm"
                    onClick={() => {
                      fetchHolidayInfo(displayYear, plantId);
                    }}
                  >
                    {isHolidayLoading ? "読み込み中..." : "表示更新"}
                  </Button>
                </Col>
              </Row>
            </Col>

            <Col md={3} xxl={4} className="d-flex ">
              <Button
                className="calendarButton btn-sm"
                onClick={() => handleYearClick(prevYear)}
              >
                {prevYear}
              </Button>
              <Button
                className="calendarButton btn-sm"
                onClick={() => handleYearClick(year)}
              >
                {year}
              </Button>
              <Button
                className="calendarButton btn-sm"
                onClick={() => handleYearClick(nextYear)}
              >
                {nextYear}
              </Button>
            </Col>
          </Row>

          <Row className="d-flex">
            <Col
              className="hide-scrollbar px-0 calHeight"
              ref={scrollRef}
              onScroll={onScroll}
              onWheel={onScroll}
            >
              <div className="custom-calendar ">
                {monthsToRender.map((monthIndex, i) => {
                  const correctedYear =
                    monthIndex < 12 ? displayYear : displayYear + 1;
                  const monthNumber = monthIndex % 12;
                  const monthDate = new Date(correctedYear, monthNumber, 1);

                  return renderMonthBox(monthDate, i, monthIndex);
                })}
              </div>
            </Col>
          </Row>
        </>
      )}

      <CalendarModal
        isOpen={isModalOpen}
        toggle={closeModal}
        selectedMonth={selectedMonth}
        generateMonthGrid={generateMonthGrid}
        formatDateKey={formatDateKey}
        holidayMap={holidayMap}
        setHolidayMap={setHolidayMap}
        weekdayLabels={weekdayLabels}
        fetchHolidayInfo={fetchHolidayInfo}
        plantId={plantId}
      />

      <EquipmentViewModal
        isOpen={openEqViewModal}
        toggle={handleCloseView}
        data={selectedEquip}
        modalPos={modalPos}
        arrowTop={arrowTop}
        source="customCalendar"
        eventColorMap={eventColorMap}
      />
    </>
  );
};

export default CustomCalendarPage;
